import { API_METHODS } from 'constants/general';
import {
  ADMIN_AGENT_MODELS,
  ADMIN_AGENT_TEMPLATE,
  ADMIN_AGENT_TEMPLATE_COMPANIES,
  ADMIN_AGENT_TEMPLATE_RESTORE,
  ADMIN_AGENT_TEMPLATE_SETTINGS,
  ADMIN_AGENT_TEMPLATE_VERSION,
  ADMIN_AGENT_TEMPLATE_VERSION_RESTORE,
  ADMIN_AGENT_TEMPLATE_VERSIONS,
  ADMIN_AGENT_TEMPLATES,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import AGENT_TEMPLATES_ADMIN_LIST_TAG, {
  AGENT_TEMPLATES_ADMIN_RECORD_TAG,
  AGENT_TEMPLATES_ADMIN_VERSIONS_TAG,
} from 'store/tags/agentTemplatesAdmin';

import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type IAgentModel from '../interfaces/IAgentModel';
import type {
  IAgentTemplate,
  IAgentTemplateDeleteResult,
  IAgentTemplateListParams,
  IAgentTemplateVersion,
  ICreateAgentTemplateRequest,
  IReplaceAgentTemplateCompaniesRequest,
  IRestoreAgentTemplateVersionRequest,
  IUpdateAgentTemplateSettingsRequest,
} from '../interfaces/IAgentTemplate';

export const agentTemplatesAdminApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getAgentModels: build.query<
    IBaseQueryResponse<IAgentModel[]>,
    { includeId?: number } | void
    >({
      query: (params) => ({
        url: ADMIN_AGENT_MODELS,
        params: params?.includeId != null ? { includeId: params.includeId } : undefined,
      }),
    }),

    getAgentTemplates: build.query<
    IBaseQueryPaginationResponse<IAgentTemplate[]>,
    IAgentTemplateListParams
    >({
      query: ({
        offset = 0,
        limit = 20,
        q,
        uiAgentTypes,
        type,
        subType,
        enabled,
        companyIds,
        includeDeleted,
      }) => ({
        url: ADMIN_AGENT_TEMPLATES,
        params: {
          offset,
          limit,
          ...(q ? { q } : {}),
          ...(uiAgentTypes?.length ? { uiAgentTypes } : {}),
          ...(type ? { type } : {}),
          ...(subType ? { subType } : {}),
          ...(enabled === undefined ? {} : { enabled }),
          ...(companyIds?.length ? { companyIds } : {}),
          ...(includeDeleted ? { includeDeleted: true } : {}),
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length
          ? [
            ...result.data.items.map(({ id }) => (
              { type: AGENT_TEMPLATES_ADMIN_LIST_TAG, id } as const
            )),
            AGENT_TEMPLATES_ADMIN_LIST_TAG,
          ]
          : [AGENT_TEMPLATES_ADMIN_LIST_TAG]
      ),
    }),

    getAgentTemplate: build.query<IBaseQueryResponse<IAgentTemplate>, { id: number, includeDeleted?: boolean }>({
      query: ({ id, includeDeleted }) => ({
        url: ADMIN_AGENT_TEMPLATE.replace('{id}', `${id}`),
        params: includeDeleted ? { includeDeleted: true } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: AGENT_TEMPLATES_ADMIN_RECORD_TAG, id },
        { type: AGENT_TEMPLATES_ADMIN_LIST_TAG, id },
      ],
    }),

    createAgentTemplate: build.mutation<IBaseQueryResponse<IAgentTemplate>, ICreateAgentTemplateRequest>({
      query: (body) => ({
        url: ADMIN_AGENT_TEMPLATES,
        method: API_METHODS.post,
        body,
      }),
      invalidatesTags: [AGENT_TEMPLATES_ADMIN_LIST_TAG],
    }),

    updateAgentTemplateSettings: build.mutation<
    IBaseQueryResponse<IAgentTemplate>,
    IUpdateAgentTemplateSettingsRequest
    >({
      query: ({ id, body }) => ({
        url: ADMIN_AGENT_TEMPLATE_SETTINGS.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        AGENT_TEMPLATES_ADMIN_LIST_TAG,
        { type: AGENT_TEMPLATES_ADMIN_RECORD_TAG, id },
        { type: AGENT_TEMPLATES_ADMIN_VERSIONS_TAG, id },
      ],
    }),

    getAgentTemplateVersions: build.query<IBaseQueryResponse<IAgentTemplateVersion[]>, number>({
      query: (id) => ({
        url: ADMIN_AGENT_TEMPLATE_VERSIONS.replace('{id}', `${id}`),
      }),
      providesTags: (_result, _error, id) => [{ type: AGENT_TEMPLATES_ADMIN_VERSIONS_TAG, id }],
    }),

    getAgentTemplateVersion: build.query<
    IBaseQueryResponse<IAgentTemplateVersion>,
    { id: number, versionNumber: number }
    >({
      query: ({ id, versionNumber }) => ({
        url: ADMIN_AGENT_TEMPLATE_VERSION
          .replace('{id}', `${id}`)
          .replace('{versionNumber}', `${versionNumber}`),
      }),
    }),

    restoreAgentTemplateVersion: build.mutation<
    IBaseQueryResponse<IAgentTemplate>,
    IRestoreAgentTemplateVersionRequest
    >({
      query: ({ id, versionNumber }) => ({
        url: ADMIN_AGENT_TEMPLATE_VERSION_RESTORE
          .replace('{id}', `${id}`)
          .replace('{versionNumber}', `${versionNumber}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        AGENT_TEMPLATES_ADMIN_LIST_TAG,
        { type: AGENT_TEMPLATES_ADMIN_RECORD_TAG, id },
        { type: AGENT_TEMPLATES_ADMIN_VERSIONS_TAG, id },
      ],
    }),

    replaceAgentTemplateCompanies: build.mutation<
    IBaseQueryResponse<IAgentTemplate>,
    IReplaceAgentTemplateCompaniesRequest
    >({
      query: ({ id, companyIds }) => ({
        url: ADMIN_AGENT_TEMPLATE_COMPANIES.replace('{id}', `${id}`),
        method: API_METHODS.put,
        body: { companyIds },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        AGENT_TEMPLATES_ADMIN_LIST_TAG,
        { type: AGENT_TEMPLATES_ADMIN_RECORD_TAG, id },
      ],
    }),

    deleteAgentTemplate: build.mutation<
    IBaseQueryResponse<IAgentTemplateDeleteResult>,
    number
    >({
      query: (id) => ({
        url: ADMIN_AGENT_TEMPLATE.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [AGENT_TEMPLATES_ADMIN_LIST_TAG],
    }),

    restoreAgentTemplate: build.mutation<IBaseQueryResponse<IAgentTemplate>, number>({
      query: (id) => ({
        url: ADMIN_AGENT_TEMPLATE_RESTORE.replace('{id}', `${id}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: (_result, _error, id) => [
        AGENT_TEMPLATES_ADMIN_LIST_TAG,
        { type: AGENT_TEMPLATES_ADMIN_RECORD_TAG, id },
      ],
    }),
  }),
});

export const {
  useGetAgentModelsQuery,
  useGetAgentTemplatesQuery,
  useGetAgentTemplateQuery,
  useCreateAgentTemplateMutation,
  useUpdateAgentTemplateSettingsMutation,
  useGetAgentTemplateVersionsQuery,
  useGetAgentTemplateVersionQuery,
  useRestoreAgentTemplateVersionMutation,
  useReplaceAgentTemplateCompaniesMutation,
  useDeleteAgentTemplateMutation,
  useRestoreAgentTemplateMutation,
} = agentTemplatesAdminApi;

export default agentTemplatesAdminApi;
